import { useMutation } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { UpsertFollowupDraftMessageDocument } from '~/graphql/generated/graphql';
import { messageModalState } from '~/state/partials/message_modal/atoms';
import { formatISOString, toYYYYMMDDhyphen } from '~/utils/date';

import { Fields } from '../../MessageForm/InputPane/types';

export const useUpsertDraftMessage = () => {
  const [isUpserting, setIsUpserting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [{ profile, patientId }, setMessageModalState] = useAtom(messageModalState);
  const [upsertDraftMessage, { data }] = useMutation(UpsertFollowupDraftMessageDocument);

  useEffect(() => {
    if (!data) return;

    setMessageModalState((state) => ({
      ...state,
      draftMessageId:
        data.upsertMedicationFollowupDraftMessage?.medicationFollowupDraftMessage.id || null,
    }));
  }, [data, setMessageModalState]);

  const upsert = useCallback(
    async (values: Fields, draftMessageId: string | null) => {
      setIsUpserting(true);
      setError(null);

      const sendDate =
        values.fixed && values.sendDate
          ? formatISOString(`${toYYYYMMDDhyphen(values.sendDate)}`)
          : null;
      const sendTime = values.fixed && values.sendTime ? values.sendTime : null;
      const medicationFollowupQuestionnaireId =
        values.attachment === 'questionnaire_sheet'
          ? values.medicationFollowupQuestionnaireId || null
          : null;

      try {
        await upsertDraftMessage({
          variables: {
            input: {
              medicationFollowupDraftMessageId: draftMessageId,
              patientId: profile?.type === 'Patient' ? patientId : null,
              guestPatientId:
                profile?.type === 'DirectVisitor' ||
                profile?.type === 'WebVisitor' ||
                profile?.type === 'LineVisitor'
                  ? patientId
                  : null,
              data: {
                sendDate,
                sendTime,
                message: values.message,
                medicationFollowupQuestionnaireId,
                csClinicId: values.attachment === 'clinic' ? values.csClinicId : null,
                fixedDate: values.fixed,
                checkboxRecommendation: values.attachment === 'clinic',
                checkboxAutoReplySetting: values.isEnabledAutoReplySetting,
                checkboxAutoReplySettingQuestionnaire: values.withAutoReplyQuestionnaire,
                autoReplySettingMessage: values.autoReplyMessage || null,
              },
            },
          },
        });
        setIsUpserting(false);
      } catch (_error) {
        setError(_error?.message || 'エラーが発生しました');
        setIsUpserting(false);
        throw new Error('', { cause: _error });
      }
    },
    [patientId, profile?.type, upsertDraftMessage],
  );

  return {
    upserting: isUpserting,
    error,
    upsert,
  };
};
